import styled, { css } from 'styled-components';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { borders, boxShadows, colors, spaces } from '@99designs/emu/settings';
import { Option } from '../TileSelect';

export interface TileSelectItemProps<T> {
    handleChange: (value: T) => void;
    option: Option<T>;
    name: string;
    isSelected: boolean;
    matchItemHeight: boolean;
    type?: 'radio' | 'checkbox';
}

const royalBlue = `#4856DF`;
const skyBlue = `#3EC1F3`;
const toRightGradientVibrantBlue = `linear-gradient(to right, ${royalBlue}, ${skyBlue});`;

interface LabelProps {
    isSelected: boolean;
    matchItemHeight: boolean;
}

const gradientBorderMixin = css`
    background-image: ${(props: { isSelected: boolean }) =>
        props.isSelected ? toRightGradientVibrantBlue : `none`};
    padding: 3px;
`;

const GradientBorderInner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    background: ${colors.white};
    height: inherit;
    border-radius: ${borders.borderRadiuses.small}px;
    justify-content: center;

    ${mediaQuery.only('small')} {
        flex-direction: row;
        justify-content: flex-start;
    }
`;

const IconContainer = styled(Box)`
    ${mediaQuery.minimum('medium')} {
        margin-top: ${spaces['1bu']};
    }
    ${mediaQuery.only('small')} {
        margin-left: ${spaces['1bu']};
    }
`;

const Label = styled.label<LabelProps>`
    box-shadow: ${({ isSelected }) => (isSelected ? boxShadows.medium : boxShadows.low)};
    border: ${({ isSelected }) => (isSelected ? `none` : colors.grey300)};
    border-radius: ${borders.borderRadiuses.medium}px;
    ${gradientBorderMixin}

    display: block;
    cursor: pointer;
    height: ${({ matchItemHeight }) => (matchItemHeight ? '100%' : 'inherit')};

    &:hover {
        box-shadow: ${boxShadows.medium};
    }
`;

export function TileSelectItem<T>({
    handleChange,
    isSelected,
    option,
    name,
    matchItemHeight,
    type = 'radio',
}: TileSelectItemProps<T>) {
    return (
        <Label
            isSelected={isSelected}
            tabIndex={0}
            onKeyPress={(e) => {
                e.preventDefault();

                if (e.key === 'Enter') {
                    handleChange(option.value);
                }
            }}
            matchItemHeight={matchItemHeight}
        >
            <GradientBorderInner>
                {option.icon && (
                    <IconContainer
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="64px"
                        height="64px"
                    >
                        {option.icon}
                    </IconContainer>
                )}
                <input
                    name={name}
                    type={type}
                    hidden
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    defaultValue={option.value as any}
                    checked={isSelected}
                    data-testid={'option-input'}
                    onChange={() => {
                        handleChange(option.value);
                    }}
                />

                {option.label}
            </GradientBorderInner>
        </Label>
    );
}
